import useSWR from "swr";
import useUser from "../context/userHook";
import useTokenHook from "../context/useTokenHook";
import { fetchDetailedUsage } from "../../utils/requests/usageRequest";
import useRevalidateOnAddItemCompleted from "../ui/useRevalidateOnAddItemCompleted";

/**
 * Matches the board-details cache's fresh window in simb-app-ms. The synced
 * rows behind the table are read per request there, so a revalidation always
 * reflects what is currently synced.
 */
const DETAILED_USAGE_DEDUPING_INTERVAL_MS = 120_000;

export interface DetailedUsageBoard {
  boardId: string;
  boardName: string;
  items: { itemId: string; itemName: string; created_at: string }[];
}

/**
 * Shared by the Detailed Usage Report page and the Settings usage panel, which
 * render the same table — one key means one fetch of an expensive endpoint.
 */
const useDetailedUsageTable = () => {
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();

  const { isLoading, error, data, mutate, isValidating } = useSWR<DetailedUsageBoard[]>(
    token && userId && accountId ? `detailedUsage:${accountId}-${userId}` : null,
    () => fetchDetailedUsage(token!),
    { revalidateOnFocus: false, dedupingInterval: DETAILED_USAGE_DEDUPING_INTERVAL_MS },
  );

  useRevalidateOnAddItemCompleted(mutate);

  return {
    isLoading,
    error,
    activeBoards: data ?? [],
    mutateDetailedUsage: mutate,
    isValidating,
  };
};

export default useDetailedUsageTable;
